import React, { useState } from 'react';
import dayjs from 'dayjs';
import { InfoOutlined } from '@mui/icons-material';
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  buildServiceCityMatrix,
  capitalizeReportLabel,
  isReportGeographyVisible,
} from './weeklyReportUtils';

const BLUE = '#0647a6';
const BORDER = '#d6dce5';
const TABLE_BLUE = '#0755bd';
const TABLE_BORDER = '#d5d8dc';
const SUMMARY_TOOLTIPS = {
  totalExpertCount: 'Total experts onboarded to date.',
  activeExpertCount: 'Experts with an approved profile.',
  expertsAddedThisWeek: 'Experts onboarded within the selected date range.',
  totalBookings: 'Bookings scheduled within the selected date range.',
  completedBookings: 'Bookings completed within the selected date range.',
  notCompletedBookings: 'Bookings in requested, accepted, or ongoing status within the selected date range.',
  missedBookings: 'All cancelled and rejected bookings within the selected date range, grouped by Customer, Expert, or Admin. Test data is excluded.',
  adminPanelBookings: 'Bookings scheduled in the selected date range and created from the Admin Panel.',
  customerAppBookings: 'Bookings scheduled in the selected date range and created by customers through the mobile app.',
  profilesSetUp: 'Experts added in the selected date range who have completed the required basic profile, service area, category, active service, and working time slot setup.',
  completeProfiles: 'Experts added in the selected date range who have completed all required profile, KYC, business, bank, service, photo, and working-time details.',
  weeklyExpertEarnings: 'Total net payable for eligible completed bookings within the selected date range, after platform fee, GST on the fee, and TCS, plus tips and adjustments.',
};

export const weeklySummaryMetrics = [
  ['totalExpertCount', 'Total Expert Count'],
  ['activeExpertCount', 'Active Expert Count'],
  ['expertsAddedThisWeek', 'Experts Added'],
  ['totalBookings', 'Total Bookings'],
  ['completedBookings', 'Completed Bookings'],
  ['notCompletedBookings', 'Not Completed Bookings'],
  ['missedBookings', 'Cancelled Bookings'],
  ['adminPanelBookings', 'Bookings from Admin Panel'],
  ['customerAppBookings', 'Bookings through App / Customer'],
  ['profilesSetUp', 'Profiles Set Up as Required'],
  ['completeProfiles', 'Complete Profiles'],
  ['weeklyExpertEarnings', 'Weekly Expert Earnings'],
];

export const bookingStatsHierarchyMetrics = [
  ['totalBookings', '1. Total Booking', 0],
  ['completedBookings', '1.1 Booking Completed', 1],
  ['notCompletedBookings', '1.2 Booking Not Completed', 1],
  ['acceptedBookings', 'Booking in Accepted state', 2],
  ['pendingBookings', 'Booking in Requested state', 2],
  ['ongoingBookings', 'Booking in Ongoing state', 2],
  ['totalBookingMissed', '1.3 Total Cancelled Bookings', 1],
  ['missedCancelledBookings', 'Cancelled Bookings', 2],
  ['cancelledByCustomer', 'Cancelled by Customer', 3],
  ['selfBookingCancelledByCustomer', 'Self / App booking cancelled by Customer', 4],
  ['adminBookingCancelledByCustomer', 'Admin booking cancelled by Customer', 4],
  ['cancelledByExpert', 'Cancelled by Expert', 3],
  ['selfBookingCancelledByExpert', 'Self / App booking cancelled by Expert', 4],
  ['adminBookingCancelledByExpert', 'Admin booking cancelled by Expert', 4],
  ['missedRejectedBookings', 'Rejected Bookings', 2],
  ['rejectedByCustomer', 'Rejected by Customer', 3],
  ['selfBookingRejectedByCustomer', 'Self / App booking rejected by Customer', 4],
  ['adminBookingRejectedByCustomer', 'Admin booking rejected by Customer', 4],
  ['rejectedByExpert', 'Rejected by Expert', 3],
  ['selfBookingRejectedByExpert', 'Self / App booking rejected by Expert', 4],
  ['adminBookingRejectedByExpert', 'Admin booking rejected by Expert', 4],
];

const formatCount = (value) => Number(value || 0).toLocaleString('en-IN');
const formatCurrency = (value) => `₹${Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const formatSummaryValue = (key, value) => {
  if (key === 'weeklyExpertEarnings') return formatCurrency(value);
  return formatCount(value);
};

const Section = ({ number, title, children }) => (
  <Box sx={{ width: '100%', minWidth: 0, maxWidth: '100%', border: `1px solid ${BORDER}`, borderRadius: '6px', mb: 1.25, overflow: 'hidden', boxSizing: 'border-box' }}>
    <Typography sx={{ px: 1.5, py: 1, color: BLUE, fontWeight: 700, fontSize: '17px' }}>
      {number}. {title}
    </Typography>
    <Box sx={{ width: '100%', minWidth: 0, maxWidth: '100%', px: 1.5, pb: 1.5, boxSizing: 'border-box' }}>{children}</Box>
  </Box>
);

const SummaryGrid = ({ summary }) => {
  const [openTooltip, setOpenTooltip] = useState(null);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
        borderTop: `1px solid ${BORDER}`,
        borderLeft: `1px solid ${BORDER}`,
      }}
    >
      {weeklySummaryMetrics.map(([key, label]) => {
      if (key === 'notCompletedBookings') {
        return (
          <Box
            key={key}
            sx={{
              minHeight: 108,
              gridColumn: { xs: '1 / -1', sm: 'span 2', md: 'span 1' },
              borderRight: `1px solid ${BORDER}`,
              borderBottom: `1px solid ${BORDER}`,
            }}
          >
            <NotCompletedBookingBox summary={summary} />
          </Box>
        );
      }

      if (key === 'missedBookings') {
        return (
          <Box
            key={key}
            sx={{
              minHeight: 108,
              gridColumn: { xs: '1 / -1', sm: 'span 2', md: 'span 1' },
              borderRight: `1px solid ${BORDER}`,
              borderBottom: `1px solid ${BORDER}`,
            }}
          >
            <MissedBookingBox summary={summary} />
          </Box>
        );
      }

      const labelElement = (
        <Typography sx={{ color: BLUE, fontSize: '12px', fontWeight: 600, lineHeight: 1.25 }}>
          {label}
        </Typography>
      );

      return (
        <Box
          key={key}
          sx={{
            minHeight: 92,
            px: 1,
            py: 1.5,
            textAlign: 'center',
            borderRight: `1px solid ${BORDER}`,
            borderBottom: `1px solid ${BORDER}`,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          {SUMMARY_TOOLTIPS[key] ? (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: .25 }}>
              {labelElement}
              <Tooltip
                title={SUMMARY_TOOLTIPS[key]}
                arrow
                placement="top"
                open={openTooltip === key}
                onClose={() => setOpenTooltip(null)}
                disableHoverListener
                disableFocusListener
                disableTouchListener
              >
                <IconButton
                  data-html2canvas-ignore="true"
                  aria-label={`View ${label} information`}
                  size="small"
                  onClick={() => setOpenTooltip(current => current === key ? null : key)}
                  sx={{ p: 0, color: BLUE, cursor: 'default' }}
                >
                  <InfoOutlined sx={{ fontSize: '14px' }} />
                </IconButton>
              </Tooltip>
            </Box>
          ) : labelElement}
          <Typography sx={{ color: BLUE, mt: 1, fontSize: '23px', lineHeight: 1, fontWeight: 700 }}>
            {formatSummaryValue(key, summary[key])}
          </Typography>
        </Box>
      );
      })}
    </Box>
  );
};

function NotCompletedBookingBox({ summary }) {
  const statuses = [
    ['Accepted', summary.acceptedBookings],
    ['Requested', summary.pendingBookings],
    ['Ongoing', summary.ongoingBookings],
  ];

  return (
    <Box
      aria-label="Not completed booking summary"
      sx={{
        minWidth: 0,
        minHeight: 108,
        height: 'auto',
        overflow: 'visible',
        bgcolor: '#fff',
      }}
    >
      <Box
        sx={{
          minHeight: 32,
          px: .8,
          py: .6,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          bgcolor: '#f5f8fc',
          borderBottom: `1px solid ${BORDER}`,
        }}
      >
        <Typography sx={{ color: BLUE, fontSize: '12px', lineHeight: 1.35, fontWeight: 600 }}>
          Not Completed Bookings
        </Typography>
        <Typography sx={{ flexShrink: 0, color: BLUE, fontSize: '12px', lineHeight: 1.35, fontWeight: 700 }}>
          {formatCount(summary.notCompletedBookings)}
        </Typography>
      </Box>

      <Box
        sx={{
          minHeight: 74,
          px: .6,
          py: .6,
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          gridTemplateRows: 'repeat(2, minmax(31px, auto))',
          rowGap: .25,
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        {statuses.map(([label]) => (
          <Typography key={label} sx={{ color: BLUE, fontSize: '12px', lineHeight: 1.35, fontWeight: 500 }}>
            {label}
          </Typography>
        ))}
        {statuses.map(([label, value]) => (
          <Typography key={label} sx={{ color: '#1d2733', fontSize: '11px', lineHeight: 1.35, fontWeight: 700 }}>
            {formatCount(value)}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}

function MissedBookingBox({ summary }) {
  const customerTotal = Number(summary.cancelledByCustomer || 0)
    + Number(summary.rejectedByCustomer || 0);
  const expertTotal = Number(summary.cancelledByExpert || 0)
    + Number(summary.rejectedByExpert || 0);
  const combinedTotal = Number(summary.totalBookingMissed || 0);

  return (
    <Box
      aria-label="Cancelled booking summary"
      sx={{
        minWidth: 0,
        minHeight: 108,
        height: 'auto',
        overflow: 'visible',
        bgcolor: '#fff',
      }}
    >
      <Box
        sx={{
          minHeight: 32,
          px: .8,
          py: .6,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          bgcolor: '#f5f8fc',
          borderBottom: `1px solid ${BORDER}`,
        }}
      >
        <Typography sx={{ color: BLUE, fontSize: '12px', lineHeight: 1.35, fontWeight: 600 }}>
          Cancelled Bookings
        </Typography>
        <Typography sx={{ flexShrink: 0, color: BLUE, fontSize: '12px', lineHeight: 1.35, fontWeight: 700 }}>
          {formatCount(combinedTotal)}
        </Typography>
      </Box>

      <Box
        sx={{
          minHeight: 74,
          px: .6,
          py: .6,
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gridTemplateRows: 'repeat(2, minmax(31px, auto))',
          rowGap: .25,
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <Typography sx={{ color: BLUE, fontSize: '12px', lineHeight: 1.35, fontWeight: 500 }}>
          Customer
        </Typography>
        <Typography sx={{ color: BLUE, fontSize: '12px', lineHeight: 1.35, fontWeight: 500 }}>
          Expert
        </Typography>
        <Typography sx={{ color: '#1d2733', fontSize: '11px', lineHeight: 1.35, fontWeight: 700 }}>
          {formatCount(customerTotal)}
        </Typography>
        <Typography sx={{ color: '#1d2733', fontSize: '11px', lineHeight: 1.35, fontWeight: 700 }}>
          {formatCount(expertTotal)}
        </Typography>
      </Box>
    </Box>
  );
}

const getRowSpan = (rows, rowIndex, key, parentKeys = []) => {
  if (!key) return 1;
  const groupingKeys = [...parentKeys, key];
  const normalizeGroupValue = (value) => String(value ?? '').trim().toLocaleLowerCase();
  const isSameGroup = (firstRow, secondRow) => groupingKeys.every(
    (groupKey) => normalizeGroupValue(firstRow?.[groupKey]) === normalizeGroupValue(secondRow?.[groupKey])
  );

  if (rowIndex > 0 && isSameGroup(rows[rowIndex - 1], rows[rowIndex])) return 0;
  let span = 1;
  while (rowIndex + span < rows.length && isSameGroup(rows[rowIndex + span], rows[rowIndex])) span += 1;
  return span;
};

const PdfTableValue = ({ children, nudge = 2 }) => (
  <Box
    component="span"
    data-pdf-table-value="true"
    data-pdf-nudge={nudge}
    sx={{
      display: 'block',
      position: 'relative',
      width: '100%',
      top: `-${nudge}px`,
      lineHeight: 1.3,
      whiteSpace: 'normal',
      overflow: 'visible',
      overflowWrap: 'normal',
      wordBreak: 'normal',
      textAlign: 'center',
    }}
  >
    {children}
  </Box>
);

const ReportTable = ({
  columns,
  rows,
  emptyText,
  groupBy,
  minWidth,
  columnGroupLabel,
  columnGroupStart = 0,
  totalLabelColumns = 0,
}) => {
  const groupKeys = Array.isArray(groupBy) ? groupBy : (groupBy ? [groupBy] : []);
  const isGrouped = groupKeys.length > 0;
  const hasColumnGroup = Boolean(columnGroupLabel) && columns.length > columnGroupStart;
  const renderHeaderCell = ([, label, , width], extraProps = {}) => (
    <TableCell
      key={`${label}-${extraProps.rowSpan || 1}-${extraProps.colSpan || 1}`}
      align="center"
      {...extraProps}
      sx={{
        bgcolor: TABLE_BLUE,
        color: '#fff',
        border: '1px solid rgba(255,255,255,.9)',
        py: isGrouped ? 1.15 : 1,
        px: 1,
        fontSize: isGrouped ? '12px' : '11px',
        lineHeight: 1.3,
        fontWeight: 700,
        verticalAlign: 'middle',
        ...(width && { width }),
      }}
    >
      <PdfTableValue>{label}</PdfTableValue>
    </TableCell>
  );

  return (
  <TableContainer
    component={Paper}
    variant="outlined"
    data-report-table-container="true"
    sx={{
      width: '100%',
      maxWidth: '100%',
      borderRadius: 0,
      boxShadow: 'none',
      overflowX: 'auto',
      overflowY: 'hidden',
      WebkitOverflowScrolling: 'touch',
    }}
  >
    <Table
      size="small"
      sx={{
        borderCollapse: 'collapse',
        tableLayout: isGrouped ? 'fixed' : 'auto',
        minWidth,
        '& th, & td': {
          textAlign: 'center !important',
          verticalAlign: 'middle !important',
        },
      }}
    >
      <TableHead>
        {hasColumnGroup ? (
          <>
            <TableRow>
              {columns.slice(0, columnGroupStart).map((column) => renderHeaderCell(column, { rowSpan: 2 }))}
              {renderHeaderCell(['cityGroup', columnGroupLabel], { colSpan: columns.length - columnGroupStart })}
            </TableRow>
            <TableRow>
              {columns.slice(columnGroupStart).map((column) => renderHeaderCell(column))}
            </TableRow>
          </>
        ) : (
          <TableRow>{columns.map((column) => renderHeaderCell(column))}</TableRow>
        )}
      </TableHead>
      <TableBody>
        {rows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={columns.length} align="center" sx={{ py: 2.5, px: 1, color: 'text.secondary', fontSize: '11px' }}>
              <PdfTableValue>{emptyText}</PdfTableValue>
            </TableCell>
          </TableRow>
        ) : rows.map((row, index) => (
          <TableRow key={`${row.city || 'row'}-${index}`}>
            {columns.map(([key, , formatter]) => {
              const groupIndex = groupKeys.indexOf(key);
              const isGroupColumn = groupIndex !== -1;
              const rowSpan = isGroupColumn
                ? getRowSpan(rows, index, key, groupKeys.slice(0, groupIndex))
                : 1;
              if (isGroupColumn && rowSpan === 0) return null;
              return (
                <TableCell
                  key={key}
                  rowSpan={rowSpan}
                  align="center"
                  sx={{
                    py: isGrouped ? 1 : .9,
                    px: isGrouped ? 1.25 : 1,
                    border: `1px solid ${isGrouped ? TABLE_BORDER : BORDER}`,
                    bgcolor: '#fff',
                    color: '#20252b',
                    fontSize: isGrouped ? '12px' : '11px',
                    lineHeight: 1.3,
                    verticalAlign: 'middle',
                    ...(isGroupColumn && { color: TABLE_BLUE, fontWeight: 700, textAlign: 'center' }),
                  }}
                >
                  <PdfTableValue>
                    {formatter ? formatter(row[key]) : (typeof row[key] === 'number' ? formatCount(row[key]) : row[key])}
                  </PdfTableValue>
                </TableCell>
              );
            })}
          </TableRow>
        ))}
          {rows.length > 0 && totalLabelColumns > 0 && (
            <TableRow>
              <TableCell
                colSpan={totalLabelColumns}
                align="center"
                sx={{ color: TABLE_BLUE, bgcolor: '#fff', fontWeight: 700, border: `1px solid ${TABLE_BORDER}`, py: 1, px: 1, fontSize: '12px' }}
              >
                <PdfTableValue>Total</PdfTableValue>
              </TableCell>
              {columns.slice(totalLabelColumns).map(([key, , formatter]) => {
                const total = rows.reduce((sum, row) => sum + Number(row[key] || 0), 0);
                return (
                  <TableCell key={key} align="center" sx={{ color: TABLE_BLUE, bgcolor: '#fff', fontWeight: 700, border: `1px solid ${TABLE_BORDER}`, py: 1, px: 1, fontSize: '12px' }}>
                    <PdfTableValue>{formatter ? formatter(total) : formatCount(total)}</PdfTableValue>
                  </TableCell>
                );
              })}
            </TableRow>
          )}
        </TableBody>
    </Table>
  </TableContainer>
  );
};

const categoryColumns = [
  ['Category', '17%'],
  ['Region', '18%'],
  ['Total Experts', '16%'],
  ['Active Experts', '16%'],
  ['Completed Bookings', '18%'],
  ['Cancelled Bookings', '15%'],
];

const CATEGORY_LINE_HEIGHT = 15;
const getCategoryLineCount = (value, charactersPerLine = 22) => (
  Math.max(1, Math.ceil(String(value || '').length / charactersPerLine))
);
const getCategoryRowHeight = (geography) => (
  Math.max(32, (getCategoryLineCount(geography) * CATEGORY_LINE_HEIGHT) + 12)
);

const GroupedCategoryTable = ({ rows, totals }) => {
  const normalizedRows = rows.filter((row) => (
    isReportGeographyVisible(row.geography)
  )).map((row) => {
    const category = capitalizeReportLabel(row.category, 'Uncategorized');
    const geography = String(row.geography).trim();
    return { ...row, category, geography };
  });
  const groups = normalizedRows.reduce((result, row) => {
    const existingGroup = result.find((group) => group.category === row.category);
    if (existingGroup) existingGroup.rows.push(row);
    else result.push({ category: row.category, rows: [row] });
    return result;
  }, []);
  const uniqueTotals = [
    totals?.totalExpertCount,
    totals?.activeExpertCount,
    totals?.completedBookings,
    totals?.missedBookings,
  ].map((value) => Number(value || 0));

  const cellSx = {
    height: 32,
    minHeight: 32,
    px: 0,
    py: 0,
    boxSizing: 'border-box',
    borderRight: `1px solid ${TABLE_BORDER}`,
    borderBottom: `1px solid ${TABLE_BORDER}`,
    bgcolor: '#fff',
    color: '#20252b',
    fontSize: '12px',
    lineHeight: 1.25,
    whiteSpace: 'normal',
    overflow: 'visible',
    position: 'relative',
    textAlign: 'center',
  };

  const CenteredDataCell = ({ width, children, height = 32, lines = 1, extraSx = {} }) => {
    const contentTop = Math.max(4, Math.floor((height - (lines * CATEGORY_LINE_HEIGHT)) / 2));

    return (
      <Box data-pdf-centered-cell="true" sx={{ ...cellSx, width, height, minHeight: height, ...extraSx }}>
        <Box
          component="span"
          data-pdf-centered-value="true"
          data-pdf-top={contentTop}
          sx={{
            position: 'absolute',
            top: `${contentTop}px`,
            left: 0,
            width: '100%',
            px: 1,
            boxSizing: 'border-box',
            lineHeight: 1.25,
            whiteSpace: 'normal',
            overflow: 'visible',
            overflowWrap: 'anywhere',
            textAlign: 'center',
            transform: 'none',
          }}
        >
          {children}
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ width: '100%', borderTop: `1px solid ${TABLE_BORDER}`, borderLeft: `1px solid ${TABLE_BORDER}` }}>
      <Box sx={{ display: 'flex', width: '100%' }}>
        {categoryColumns.map(([label, width]) => (
          <CenteredDataCell
            key={label}
            width={width}
            height={36}
            extraSx={{
              bgcolor: TABLE_BLUE,
              color: '#fff',
              borderColor: 'rgba(255,255,255,.9)',
              fontWeight: 700,
            }}
          >
            {label}
          </CenteredDataCell>
        ))}
      </Box>

      {groups.length === 0 ? (
        <CenteredDataCell width="100%" height={58} extraSx={{ color: 'text.secondary' }}>
          No category and geography data is available
        </CenteredDataCell>
      ) : groups.map((group) => {
        const preparedRows = group.rows.map((row) => {
          const geographyLines = getCategoryLineCount(row.geography);
          return { row, geographyLines, rowHeight: getCategoryRowHeight(row.geography) };
        });
        const groupHeight = preparedRows.reduce((sum, item) => sum + item.rowHeight, 0);
        const categoryLines = getCategoryLineCount(group.category, 18);

        return (
          <Box key={group.category} sx={{ display: 'flex', width: '100%' }}>
            <CenteredDataCell
              width="17%"
              height={groupHeight}
              lines={categoryLines}
              extraSx={{ color: TABLE_BLUE, fontWeight: 700 }}
            >
              {group.category}
            </CenteredDataCell>
            <Box sx={{ width: '83%', height: groupHeight }}>
              {preparedRows.map(({ row, geographyLines, rowHeight }, index) => {

                return (
                  <Box key={`${group.category}-${row.geography}-${index}`} sx={{ display: 'flex', width: '100%', height: rowHeight }}>
                    <CenteredDataCell width="21.6867%" height={rowHeight} lines={geographyLines}>{row.geography}</CenteredDataCell>
                    <CenteredDataCell width="19.2771%" height={rowHeight}>{formatCount(row.totalExperts)}</CenteredDataCell>
                    <CenteredDataCell width="19.2771%" height={rowHeight}>{formatCount(row.activeExperts)}</CenteredDataCell>
                    <CenteredDataCell width="21.6867%" height={rowHeight}>{formatCount(row.executedBookings)}</CenteredDataCell>
                    <CenteredDataCell width="18.0724%" height={rowHeight}>{formatCount(row.missedBookings)}</CenteredDataCell>
                  </Box>
                );
              })}
            </Box>
          </Box>
        );
      })}

      {groups.length > 0 && (
        <Box sx={{ display: 'flex', width: '100%', color: TABLE_BLUE, fontWeight: 700 }}>
          <CenteredDataCell width="35%" extraSx={{ color: TABLE_BLUE, fontWeight: 700 }}>Overall Total</CenteredDataCell>
          <CenteredDataCell width="16%" extraSx={{ color: TABLE_BLUE, fontWeight: 700 }}>{formatCount(uniqueTotals[0])}</CenteredDataCell>
          <CenteredDataCell width="16%" extraSx={{ color: TABLE_BLUE, fontWeight: 700 }}>{formatCount(uniqueTotals[1])}</CenteredDataCell>
          <CenteredDataCell width="18%" extraSx={{ color: TABLE_BLUE, fontWeight: 700 }}>{formatCount(uniqueTotals[2])}</CenteredDataCell>
          <CenteredDataCell width="15%" extraSx={{ color: TABLE_BLUE, fontWeight: 700 }}>{formatCount(uniqueTotals[3])}</CenteredDataCell>
        </Box>
      )}
    </Box>
  );
};

const WeeklyReportDocument = React.forwardRef(({ report, pdfLayout = false }, ref) => {
  const summary = report?.summary || {};
  const period = report?.period || {};
  const topPerformingExperts = (report?.topPerformingExperts || [])
    .slice(0, 10)
    .map((row, index) => ({ ...row, rank: index + 1 }));
  const earningsByCategory = (report?.earningsByCategory || [])
    .filter((row) => Number(row.weeklyEarnings) > 0);
  const earningsByGeography = (report?.earningsByGeography || [])
    .filter((row) => (
      Number(row.weeklyEarnings) > 0 && isReportGeographyVisible(row.geography)
    ));
  const serviceCityMatrix = buildServiceCityMatrix(report?.servicesByCity || []);
  const pdfCityChunks = [];
  for (let index = 0; index < serviceCityMatrix.cities.length; index += 3) {
    pdfCityChunks.push({
      startIndex: index,
      cities: serviceCityMatrix.cities.slice(index, index + 3),
    });
  }
  if (pdfCityChunks.length === 0) pdfCityChunks.push({ startIndex: 0, cities: [] });
  const categoryGeographyTotals = report?.categoryGeographyTotals || summary;
  const periodLabel = period.startDate && period.endDate
    ? `${dayjs(period.startDate).format('D MMM')} – ${dayjs(period.endDate).format('D MMM YYYY')}`
    : '';

  return (
    <Box
      ref={ref}
      data-weekly-report-document="true"
      data-pdf-layout={pdfLayout ? 'true' : 'false'}
      sx={{
        width: pdfLayout ? 1100 : '100%',
        minWidth: pdfLayout ? 1100 : 0,
        maxWidth: pdfLayout ? 1100 : '100%',
        position: 'relative',
        margin: 0,
        boxSizing: 'border-box',
        bgcolor: '#fff',
        color: '#1d2733',
        border: `1px solid ${BORDER}`,
        p: 2.5,
        fontFamily: 'Arial, sans-serif',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, px: 1.5, pb: 2, borderBottom: `2px solid ${BLUE}` }}>
        <Box
          component="img"
          src={`${process.env.PUBLIC_URL}/cudel_logo.svg`}
          alt="CuDel"
          sx={{ width: 180, height: 58, objectFit: 'contain', objectPosition: 'left center' }}
        />
        <Box>
          <Typography sx={{ color: '#083b83', fontSize: '28px', fontWeight: 700, lineHeight: 1.2 }}>
            CuDel Weekly Operations Report
          </Typography>
          <Typography sx={{ color: '#4d5560', mt: .6, fontSize: '12px' }}>
            Week: {periodLabel} &nbsp;|&nbsp; Generated from CuDel operational data &nbsp;|&nbsp; Timezone: IST
          </Typography>
        </Box>
      </Box>

      <Box sx={{ pt: 1.5 }}>
        <Section number="1" title="Weekly Stats">
          <SummaryGrid summary={summary} />
        </Section>

        <Section number="2" title="Category & Geography Wise Expert Count (Lifetime)">
          <Box sx={{ width: '100%', maxWidth: '100%' }}>
            <GroupedCategoryTable rows={report?.categoryGeography || []} totals={categoryGeographyTotals} />
          </Box>
        </Section>

        <Section number="3" title="Top 10 Weekly Performers">
          <ReportTable
            columns={[
              ['rank', 'Rank'],
              ['expertName', 'Expert Name'],
              ['category', 'Category'],
              ['geography', 'Geography'],
              ['executedBookings', 'Executed'],
              ['missedBookings', 'Cancelled'],
              ['weeklyEarnings', 'Weekly Earnings', formatCurrency],
              ['payoutEligibility', 'Payout Eligibility', (value) => (
                <Box component="span" sx={{ color: value === 'Not eligible' ? '#d32f2f' : 'inherit' }}>
                  {value || 'Unavailable'}
                </Box>
              )],
            ]}
            rows={topPerformingExperts}
            emptyText="No performers available for this period"
          />
        </Section>

        <Section number="4" title="Weekly Earnings Summary">
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 2 }}>
            <Box>
              <Box sx={{ bgcolor: BLUE, color: '#fff', textAlign: 'center', py: 1, px: 1, fontSize: '11px', fontWeight: 700 }}>
                <PdfTableValue>Earnings by Category</PdfTableValue>
              </Box>
              <ReportTable
                columns={[
                  ['category', 'Category'],
                  ['weeklyEarnings', 'Weekly Earnings', formatCurrency],
                ]}
                rows={earningsByCategory}
                emptyText="No category earnings"
              />
            </Box>
            <Box>
              <Box sx={{ bgcolor: BLUE, color: '#fff', textAlign: 'center', py: 1, px: 1, fontSize: '11px', fontWeight: 700 }}>
                <PdfTableValue>Earnings by Geography</PdfTableValue>
              </Box>
              <ReportTable
                columns={[
                  ['geography', 'Geography'],
                  ['weeklyEarnings', 'Weekly Earnings', formatCurrency],
                ]}
                rows={earningsByGeography}
                emptyText="No geography earnings"
              />
            </Box>
          </Box>
        </Section>

        <Section number="5" title="Weekly Additions and Booking Sources">
          <Box sx={{ width: '100%', minWidth: 0, maxWidth: '100%', display: 'grid', gap: 2 }}>
            <Box sx={{ width: '100%', minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
              <Typography sx={{ color: BLUE, mb: .3, fontSize: '11px', fontWeight: 700 }}>Top 20 Expert-Registered Services by City</Typography>
              <Typography sx={{ color: '#667085', mb: .7, fontSize: '10px' }}>Cell values show unique registered experts.</Typography>
              {(pdfLayout ? pdfCityChunks : [{ startIndex: 0, cities: serviceCityMatrix.cities }]).map((chunk, chunkIndex) => (
                <Box key={`${chunk.startIndex}-${chunk.cities.join('|')}`} sx={{ minWidth: 0, mb: pdfLayout && chunkIndex < pdfCityChunks.length - 1 ? 1.25 : 0 }}>
                  {pdfLayout && pdfCityChunks.length > 1 && (
                    <Typography sx={{ color: '#667085', mb: .4, fontSize: '9px', fontWeight: 600 }}>
                      Cities {chunk.startIndex + 1}–{chunk.startIndex + chunk.cities.length}
                    </Typography>
                  )}
                  <ReportTable
                    columns={[
                      ['category', 'Category', null, 160],
                      ['subcategory', 'Subcategory', null, 170],
                      ['service', 'Service', null, 310],
                      ...chunk.cities.map((city, index) => [`city_${chunk.startIndex + index}`, city, null, 125]),
                    ]}
                    rows={serviceCityMatrix.rows}
                    groupBy={['category', 'subcategory']}
                    minWidth={640 + (chunk.cities.length * 125)}
                    columnGroupLabel="CITIES"
                    columnGroupStart={3}
                    totalLabelColumns={3}
                    emptyText="No services added"
                  />
                </Box>
              ))}
              <Box
                sx={{
                  mt: '-1px',
                  py: 1,
                  px: 1.5,
                  border: `1px solid ${BORDER}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1.5,
                  color: TABLE_BLUE,
                }}
              >
                <Typography sx={{ fontSize: '11px', fontWeight: 700 }}>Total Services Added</Typography>
                <Typography sx={{ fontSize: '12px', fontWeight: 700 }}>{formatCount(summary.expertServicesAdded)}</Typography>
              </Box>
            </Box>
            <Box sx={{ width: { xs: '100%', md: '100%' }, minWidth: 0, justifySelf: 'start' }}>
              <Typography sx={{ color: BLUE, mb: .7, fontSize: '11px', fontWeight: 700 }}>Weekly Bookings by Region and Source</Typography>
              <ReportTable
                columns={[
                  ['city', 'Region', null, '25%'],
                  ['totalBookings', 'Total', null, '25%'],
                  ['adminPanelBookings', 'Admin', null, '25%'],
                  ['customerAppBookings', 'App / Customer', null, '25%'],
                ]}
                rows={report?.bookingsByCity || []}
                minWidth={600}
                emptyText="No bookings created"
              />
            </Box>
          </Box>
        </Section>

        <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: '6px', overflow: 'hidden' }}>
          <Box sx={{ p: 1.5, textAlign: 'center' }}>
            <Typography sx={{ color: BLUE, fontSize: '11px', fontWeight: 600 }}>Report Period</Typography>
            <Typography sx={{ color: BLUE, fontSize: '15px', fontWeight: 700 }}>{periodLabel}</Typography>
          </Box>
        </Box>

        <Box sx={{ mt: 1.25, p: 1, textAlign: 'center', bgcolor: '#f3f7fd', border: `1px solid ${BORDER}`, borderRadius: '5px' }}>
          <Typography sx={{ color: '#174b90', fontSize: '10px' }}>
            App / Customer represents customer-created bookings.
            Weekly earnings use the expert payout net-payable amount after platform fee, GST on the fee, and TCS, plus eligible tips and adjustments.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
});

WeeklyReportDocument.displayName = 'WeeklyReportDocument';

export default WeeklyReportDocument;
